import { useCallback, useMemo, useState } from 'react';
import { AppId, FCX, getFormattedThousand, ID, ProductLogo } from '@datassential/platform-ui-lib';
import { CategoryLensSocialKeyword } from '@apiHooks/useHomeCategoryLensAllSocial';
import BarChart, { BarChartItem, BarRenderProps } from '@components/BarChart/BarChart';
import HomeSectionSubTitle from '@pages/Home/components/HomeSectionSubTitle';
import { isNumber } from "@core/formValidation";
import { NO_VALUE_PLACEHOLDER } from "@models";
import CategoryLensPostsPopup from './CategoryLensPostsPopup';

interface CategoryLensSocialProps {
    categoryName: string;
    categoryId: ID;
    keywords: CategoryLensSocialKeyword[];
    isLoading: boolean;
}

interface ActiveKeyword {
    id: number;
    name: string;
    growth: number;
}

const formatValue = (value: number) => {
    if (!isNumber(value)) return NO_VALUE_PLACEHOLDER;
    const sign = value > 0 ? '+' : '';
    return `${sign}${getFormattedThousand(Math.round(value))}%`;
};

const CategoryLensSocial: FCX<CategoryLensSocialProps> = ({ categoryName, categoryId, keywords, isLoading }) => {
    const [activeKeyword, setActiveKeyword] = useState<ActiveKeyword | null>(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const sortedKeywords = useMemo(
        () => [...keywords].sort((a, b) => b.growth30Day - a.growth30Day).slice(0, 10),
        [keywords],
    );

    const renderBar = useCallback(
        (item: BarChartItem, index: number, barProps: BarRenderProps) => (
            <button
                type="button"
                key={`bar-${item.id || index}`}
                className={barProps.className}
                style={barProps.style}
                aria-label={`View example posts for ${item.name}`}
                onClick={() => {
                    const keyword = sortedKeywords[index];
                    if (!keyword) return;
                    setActiveKeyword({
                        id: keyword.keywordId,
                        name: keyword.keyword ?? '',
                        growth: keyword.growth30Day,
                    });
                    setIsPopupOpen(true);
                }}
            >
                <div className="BarChart__bar-value">
                    {barProps.valueLabel}
                </div>
            </button>
        ),
        [sortedKeywords]
    );

    const chartData = useMemo(
        () => sortedKeywords.map((item, index) => ({
            id: Number(`${categoryId}${index}`),
            name: item.keyword ?? '',
            value: item.growth30Day,
        })),
        [sortedKeywords, categoryId],
    );

    return (
        <div className="CategoryLensSocial" data-testid="blockCategoryLensSocial">
            <div className="CategoryLensSocial__header">
                <HomeSectionSubTitle testId="textCategoryLensSocialTitle">
                    Social: {categoryName}
                </HomeSectionSubTitle>
                <span className="CategoryLensSocial__subtitle">
                    past 1-month social engagement growth within the category
                </span>
            </div>
            <div className="CategoryLensSocial__chart">
                {isLoading ? (
                    <div className="spinner spinner-blink">
                        <ProductLogo appId={AppId.Snap} type="regular"/>
                    </div>
                ) : (
                    <BarChart
                        key={categoryId}
                        className="CategoryLensSocial__bar-chart CategoryLensSocialBarChart"
                        data={chartData}
                        formatValue={formatValue}
                        renderBar={renderBar}
                    />
                )}
            </div>
            <CategoryLensPostsPopup
                isOpened={isPopupOpen}
                keywordId={activeKeyword?.id ?? null}
                keywordName={activeKeyword?.name ?? ''}
                growth={activeKeyword?.growth ?? 0}
                lrCategoryId={categoryId}
                onClose={() => setIsPopupOpen(false)}
            />
        </div>
    );
};

export default CategoryLensSocial;
